import React from "react";
import { cn } from "../../lib/utils";
import { getThemeClass } from "../../lib/theme";

interface ResumePageProps {
  email?: string;
}

const ResumePage = ({ email = "chintantank.work@gmail.com" }: ResumePageProps) => {
  return (
    <div
      className={cn(
        "max-w-5xl mx-auto p-8",
        getThemeClass("colors.background.surface")
      )}
    >
      {/* Header */}
      <header className="flex justify-between items-start mb-8">
        {/* Left - name + title */}
        <div>
          <h1
            className={cn(
              "text-4xl font-bold mb-1",
              getThemeClass("colors.text.primary")
            )}
          >
            Chintan Tank
          </h1>
          <h2
            className={cn(
              "text-lg font-semibold",
              getThemeClass("colors.primary.main")
            )}
          >
            Principal Engineer | AI Platforms &amp; CDN Infrastructure
          </h2>
        </div>

        {/* Right - contact details */}
        <div
          className={cn(
            "text-sm text-right leading-relaxed",
            getThemeClass("colors.text.secondary")
          )}
        >
          <p>San Francisco Bay Area</p>
          <p>{email}</p>
          <p>linkedin.com/in/chintantank</p>
        </div>
      </header>

      {/* Professional Summary */}
      <section className="mb-8">
        <h2
          className={cn(
            "text-xl font-semibold mb-3",
            getThemeClass("colors.primary.main"),
            "border-b",
            getThemeClass("colors.border.light")
          )}
        >
          Professional Summary
        </h2>
        <p
          className={cn(
            "text-base leading-relaxed",
            getThemeClass("colors.text.secondary")
          )}
        >
          15+ years architecting large-scale distributed systems across AI
          platforms, Content Delivery Networks, and edge computing. Proven track
          record of leading cross-functional teams, driving innovation, and
          delivering high-impact solutions serving hundreds of thousands of
          enterprise sites globally. Deep expertise in performance optimization,
          cloud infrastructure, and translating complex technical domains into
          measurable business outcomes.
        </p>
      </section>

      {/* Core Technical Skills */}
      <section className="mb-8">
        <h2
          className={cn(
            "text-xl font-semibold mb-3",
            getThemeClass("colors.primary.main"),
            "border-b",
            getThemeClass("colors.border.light")
          )}
        >
          Core Technical Skills
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <ul
              className={cn(
                "space-y-2",
                getThemeClass("colors.text.secondary")
              )}
            >
              <li>
                <span className="font-semibold">CDN & Edge Computing:</span>{" "}
                Cloudflare, Akamai, Edge Workers, Image Optimization
              </li>
              <li>
                <span className="font-semibold">Performance Engineering:</span>{" "}
                API Latency Reduction, Caching Strategies, Load Testing
              </li>
              <li>
                <span className="font-semibold">Distributed Systems:</span>{" "}
                Microservices Architecture, API Design, Scalability
              </li>
            </ul>
          </div>
          <div>
            <ul
              className={cn(
                "space-y-2",
                getThemeClass("colors.text.secondary")
              )}
            >
              <li>
                <span className="font-semibold">Cloud & Infrastructure:</span>{" "}
                AWS (EC2, S3, Lambda, SQS, SNS, SWF), Kubernetes
              </li>
              <li>
                <span className="font-semibold">Languages & Databases:</span>{" "}
                Java, Python, Ruby, ElasticSearch, Redis, Kafka
              </li>
              <li>
                <span className="font-semibold">DevOps & Monitoring:</span>{" "}
                Docker, CI/CD pipelines, Datadog, Sumologic
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Professional Experience */}
      <section className="mb-8">
        <h2
          className={cn(
            "text-xl font-semibold mb-3",
            getThemeClass("colors.primary.main"),
            "border-b",
            getThemeClass("colors.border.light")
          )}
        >
          Professional Experience
        </h2>

        {/* Salesforce */}
        <div className="mb-6">
          <div className="flex justify-between items-baseline mb-2">
            <h3
              className={cn(
                "text-lg font-semibold",
                getThemeClass("colors.text.primary")
              )}
            >
              Principal Member of Technical Staff, Salesforce
            </h3>
            <span className={getThemeClass("colors.text.secondary")}>
              Feb 2024 - Present
            </span>
          </div>
          <div className="flex justify-between items-baseline mb-2">
            <h3
              className={cn(
                "text-lg font-semibold",
                getThemeClass("colors.text.primary")
              )}
            >
              Lead Software Engineer, Salesforce
            </h3>
            <span className={getThemeClass("colors.text.secondary")}>
              Nov 2017 - Mar 2024
            </span>
          </div>
        </div>

        <div className="mb-6">
          <p
            className={cn(
              "mb-2 italic",
              getThemeClass("colors.text.secondary")
            )}
          >
            Led critical infrastructure initiatives for Salesforce's Experience
            Cloud, driving innovation in CDN integration, image optimization,
            and API caching. Architected global infrastructure serving 292,600+
            enterprise sites across 226+ countries, processing 10B+ edge
            requests biweekly.
          </p>
          <div className="mt-3">
            <h4
              className={cn(
                "font-semibold mb-3",
                getThemeClass("colors.text.primary")
              )}
            >
              Key Achievements:
            </h4>
            <ul
              className={cn(
                "space-y-2.5",
                getThemeClass("colors.text.secondary")
              )}
            >
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Architected Metadata Intelligence Platform (MDI) - an
                  AI-powered system using multi-LLM integration (GPT-4, Claude,
                  Gemini) to automate discovery and quality analysis of 26,000+
                  standard and ~1M custom Salesforce components; recognized by
                  VP-level leadership as foundational infrastructure for
                  Salesforce&apos;s Agentic AI vision
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Designed polymorphic LLM enrichment framework supporting
                  multiple metadata types through a unified processing pipeline
                  with LLM-as-a-Judge quality evaluation and fault-tolerant
                  async architecture
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Part of the six-person team that built Quick Pages, including
                  joint work on its direct-authored, no-agent LLM approach; the
                  product was unveiled at Dreamforce 2026 and is in open beta
                  through the end of October 2026
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Spearheaded image optimization for Cloudflare CDN, reducing
                  bytes served by 80% while maintaining quality
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Architected modular Workers platform enabling feature
                  deployment across multiple Salesforce accounts
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <div>
                  <p>
                    Led development of API caching infrastructure improving
                    latencies:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2">
                    <li className="flex items-start">
                      <span className="resume-bullet mr-2 mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-sky-400"></span>
                      <span>
                        Navigation Menu Items API: 77% improvement (219ms to
                        50ms)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="resume-bullet mr-2 mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-sky-400"></span>
                      <span>
                        SEO Properties API: 85% improvement (343ms to 50ms)
                      </span>
                    </li>
                  </ul>
                </div>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Designed and executed performance tests validating 13x current
                  load (4M requests/10min)
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <div>
                  <p>
                    Led Service Not Available (SNA) page workflow evolution:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2">
                    <li className="flex items-start">
                      <span className="resume-bullet mr-2 mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-sky-400"></span>
                      <span>
                        Enabled no-code creation through Experience Builder
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="resume-bullet mr-2 mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-sky-400"></span>
                      <span>
                        Achieved 59% adoption across SNA-enabled enterprise sites
                      </span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Ooyala */}
        <div className="mb-6">
          <div className="flex justify-between items-baseline mb-2">
            <h3
              className={cn(
                "text-lg font-semibold",
                getThemeClass("colors.text.primary")
              )}
            >
              Senior Software Engineer, Team Lead, Ooyala
            </h3>
            <span className={getThemeClass("colors.text.secondary")}>
              Dec 2015 - Nov 2017
            </span>
          </div>
          <div className="mt-3">
            <ul
              className={cn(
                "space-y-2.5",
                getThemeClass("colors.text.secondary")
              )}
            >
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Developed new Event Bus + Search Architecture reducing
                  indexing latency from 15min to 5ms
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Implemented streaming database changes using Zendesk/Maxwell
                  with Apache Kafka
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Improved ingestion throughput by 88%, reducing infrastructure
                  costs by 42%
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Built micro-services on Java/Spring and Ruby/Sinatra with
                  integrated logging & monitoring
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Snagfilms */}
        <div className="mb-6">
          <div className="flex justify-between items-baseline mb-2">
            <h3
              className={cn(
                "text-lg font-semibold",
                getThemeClass("colors.text.primary")
              )}
            >
              Senior Software Engineer, Snagfilms
            </h3>
            <span className={getThemeClass("colors.text.secondary")}>
              Feb 2014 - Nov 2015
            </span>
          </div>
          <div className="mt-3">
            <ul
              className={cn(
                "space-y-2.5",
                getThemeClass("colors.text.secondary")
              )}
            >
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Architected end-to-end subscription system with sophisticated
                  offers/coupons support
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Developed payments backend supporting multiple methods across
                  platforms
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Reduced latency by 421% for crawlers and 37% for standard
                  users
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Indiana University */}
        <div>
          <div className="flex justify-between items-baseline mb-2">
            <h3
              className={cn(
                "text-lg font-semibold",
                getThemeClass("colors.text.primary")
              )}
            >
              VIVO Visualization Team Lead, Indiana University
            </h3>
            <span className={getThemeClass("colors.text.secondary")}>
              May 2009 - Aug 2011
            </span>
          </div>
          <div className="mt-3">
            <ul
              className={cn(
                "space-y-2.5",
                getThemeClass("colors.text.secondary")
              )}
            >
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Led visualization architecture design for NIH-funded VIVO
                  project (Grant U24 RR029822: "VIVO: Enabling National
                  Networking of Scientists"), creating frameworks adopted by
                  research institutions nationwide with 10+ year sustained usage
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Designed visualization architecture enabling third-party
                  integration with semantic data backend
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Created sparklines, egocentric, and temporal graph
                  visualizations included in VIVO release
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Developed caching framework optimizing complex visualization
                  performance
                </span>
              </li>
              <li className="flex items-start">
                <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
                <span>
                  Contributed to NIH and NSF-funded projects including Epidemics
                  Cyberinfrastructure, Network Workbench, and Science Policy
                  Tool initiatives
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Publications & Patents */}
      <section className="mb-8 print:break-before-page">
        <h2
          className={cn(
            "text-xl font-semibold mb-3",
            getThemeClass("colors.primary.main"),
            "border-b",
            getThemeClass("colors.border.light")
          )}
        >
          Patents
        </h2>

        <div>
          <h3
            className={cn(
              "text-lg font-semibold mb-2",
              getThemeClass("colors.text.primary")
            )}
          >
            Patent
          </h3>
          <div className="mt-2">
            <p
              className={cn(
                "font-semibold",
                getThemeClass("colors.text.primary")
              )}
            >
              "System to Dynamically Share Data by Business Relationship Type"
            </p>
            <p className={getThemeClass("colors.text.secondary")}>
              US Patent 10,908,826 · Issued February 2, 2021 · Filed while at Salesforce
            </p>
            <p className={cn(
              "mt-2",
              getThemeClass('colors.text.secondary')
            )}>
              Innovative system enabling dynamic control of data access across multiple portals based on business relationship functions, allowing flexible and secure data sharing while maintaining proper access controls.
            </p>
          </div>
        </div>
      </section>

      {/* Professional Activities & Awards */}
      <section className="mb-8">
        <h2
          className={cn(
            "text-xl font-semibold mb-3",
            getThemeClass("colors.primary.main"),
            "border-b",
            getThemeClass("colors.border.light")
          )}
        >
          Professional Activities & Awards
        </h2>

        {/* Publications */}
        <div className="mb-6">
          <h3
            className={cn(
              "text-lg font-semibold mb-2",
              getThemeClass("colors.text.primary")
            )}
          >
            Publications
          </h3>
          <ul
            className={cn(
              "space-y-2.5",
              getThemeClass("colors.text.secondary")
            )}
          >
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                &quot;AI-Integrated DevSecOps for Scalable, Secure, and Modern
                Cloud Architectures&quot; - IEEE ICEARS 2026. Published on IEEE
                Xplore, March 2026. DOI: 10.1109/ICEARS67481.2026.11416586
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                &quot;Modular Edge Computing at Multi-Tenant SaaS Scale on
                Cloudflare Workers&quot; - InfoQ professional publication article.
                https://www.infoq.com/articles/modular-edge-computing/
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                &quot;Agentic CDNs: A Multi-Agent Architecture for Edge-Native AI
                Inference and Control&quot; - Published in IEEE ICAD 2026, Boston,
                June 11-12, 2026. DOI: 10.1109/ICAD69378.2026.11608667
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                &quot;Collaborative Edge-Cloud Inference for Real-Time Multimedia
                Delivery over Next-Gen CDNs&quot; - Presented via recorded
                submission in the official ICCCNet 2026 online program. Springer
                LNNS publication pending.
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                &quot;Design and Evaluation of SLM-Serv&quot; - Presented at
                ICDAM 2026, London, June 12-14, 2026. Springer LNNS publication
                pending.
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                &quot;Analyzing and Visualizing VIVO Data&quot; - Book chapter in
                VIVO: A Semantic Portal for Scholarly Networking (Springer,
                2012). Co-authored with Distinguished Professor Katy Börner,
                Indiana University.
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                &quot;Network and Topical Analysis for the Humanities using NWB
                and Sci2&quot; - Digital Humanities Conference 2011.
                Co-authored with Weingart, S.B., Börner, K., et al.
              </span>
            </li>
          </ul>
        </div>

        {/* Professional Memberships */}
        <div className="mb-6">
          <h3
            className={cn(
              "text-lg font-semibold mb-2",
              getThemeClass("colors.text.primary")
            )}
          >
            Professional Memberships
          </h3>
          <ul
            className={cn(
              "space-y-2.5",
              getThemeClass("colors.text.secondary")
            )}
          >
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                IEEE Senior Member (2025) - Institute of Electrical and
                Electronics Engineers
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                IETE Fellow (2025) - Institution of Electronics and
                Telecommunication Engineers
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                Sigma Xi Full Member (2026) - The Scientific Research Honor Society
              </span>
            </li>
          </ul>
        </div>

        {/* Technical Review & Judging */}
        <div>
          <h3
            className={cn(
              "text-lg font-semibold mb-2",
              getThemeClass("colors.text.primary")
            )}
          >
            Technical Review & Judging
          </h3>
          <ul
            className={cn(
              "space-y-2.5",
              getThemeClass("colors.text.secondary")
            )}
          >
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                Technical Program Committee (TPC) Reviewer, IEEE SoutheastCon
                2026 - Completed 5 double-blind peer reviews, Computing Systems
                and AI/Predictive Modeling tracks (January 2026)
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                Reviewer, IEEE NFV-SDN 2026 - Completed peer review for the CDN
                track
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                Reviewer, ICCISCS 2026 - Completed peer review
              </span>
            </li>
            <li className="flex items-start">
              <span className="resume-bullet mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600"></span>
              <span>
                Technical evaluation contributor for academic and industry
                publications
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* Education */}
      <section className="print:break-before-page">
        <h2
          className={cn(
            "text-xl font-semibold mb-3",
            getThemeClass("colors.primary.main"),
            "border-b",
            getThemeClass("colors.border.light")
          )}
        >
          Education
        </h2>
        <div className="space-y-2">
          <div className="flex justify-between">
            <div>
              <p
                className={cn(
                  "font-semibold",
                  getThemeClass("colors.text.primary")
                )}
              >
                Master of Science, Computer Science
              </p>
              <p className={getThemeClass("colors.text.secondary")}>
                Indiana University, Bloomington, IN
              </p>
            </div>
            <span className={getThemeClass("colors.text.secondary")}>
              2007 - 2009
            </span>
          </div>
          <div className="flex justify-between">
            <div>
              <p
                className={cn(
                  "font-semibold",
                  getThemeClass("colors.text.primary")
                )}
              >
                Bachelor of Engineering, Computer Engineering
              </p>
              <p className={getThemeClass("colors.text.secondary")}>
                University of Mumbai, India
              </p>
            </div>
            <span className={getThemeClass("colors.text.secondary")}>
              2003 - 2007
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResumePage;
